import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Wrench, Paintbrush, Check, Phone, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Prestations = () => {
  const [prestations, setPrestations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('entretien');
  const [expandedCards, setExpandedCards] = useState({});

  useEffect(() => {
    const fetchPrestations = async () => {
      try {
        const response = await axios.get(`${API}/prestations`);
        setPrestations(response.data);
      } catch (error) {
        console.error('Error fetching prestations:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPrestations();
  }, []);

  const toggleCard = (id) => {
    setExpandedCards(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const filteredPrestations = prestations.filter(p => p.category_type === activeTab && p.active);

  const equipmentOrder = ['micro_pelle', 'pelle_compacte', 'pelle_moyenne', 'tracto_pelle'];
  const sortedPrestations = [...filteredPrestations].sort((a, b) => {
    return equipmentOrder.indexOf(a.equipment_type) - equipmentOrder.indexOf(b.equipment_type);
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#F5C500] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="prestations-page">
      <SEO
        title="Prestations Entretien & Peinture Engins de Chantier La Réunion - Forfaits Détaillés"
        description="Guide complet des forfaits d'entretien et de peinture pour engins de chantier à La Réunion. Micro-pelles, pelles compactes 1-6t, pelles 6-10t, tracto-pelles. Vidange, filtres, graissage, peinture anticorrosion. IDMAT Service — Saint-Leu."
        canonical="/prestations"
        image="https://images.pexels.com/photos/5125783/pexels-photo-5125783.jpeg?auto=compress&cs=tinysrgb&w=1200"
      />
      {/* Hero Section */}
      <section className="hero-section hero-entretien min-h-[50vh]">
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/40" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="max-w-2xl animate-slide-up">
            <span 
              className="text-sm font-bold tracking-widest uppercase text-[#F5C500] block mb-4"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Nos Prestations
            </span>
            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Guide Complet des Prestations
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Découvrez nos forfaits détaillés d'entretien et de peinture pour tous types d'engins de chantier.
            </p>
          </div>
        </div>
      </section>

      {/* Tabs */}
      <section className="bg-white sticky top-20 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-0">
            <button
              onClick={() => setActiveTab('entretien')}
              className={`flex items-center gap-3 px-8 py-5 font-bold uppercase tracking-wide transition-colors border-b-4 ${
                activeTab === 'entretien' 
                  ? 'border-[#F5C500] text-[#1a1a1a] bg-gray-50' 
                  : 'border-transparent text-gray-500 hover:text-[#1a1a1a]'
              }`}
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              data-testid="tab-entretien"
            >
              <Wrench size={24} className={activeTab === 'entretien' ? 'text-[#F5C500]' : ''} />
              Entretien
            </button>
            <button
              onClick={() => setActiveTab('peinture')}
              className={`flex items-center gap-3 px-8 py-5 font-bold uppercase tracking-wide transition-colors border-b-4 ${
                activeTab === 'peinture' 
                  ? 'border-[#F5C500] text-[#1a1a1a] bg-gray-50' 
                  : 'border-transparent text-gray-500 hover:text-[#1a1a1a]'
              }`}
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              data-testid="tab-peinture"
            >
              <Paintbrush size={24} className={activeTab === 'peinture' ? 'text-[#F5C500]' : ''} />
              Peinture
            </button>
          </div>
        </div>
      </section>

      {/* Prestations List */}
      <section className="section-gray py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-4"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              {activeTab === 'entretien' ? '🔧 Prestations Entretien' : '🎨 Prestations Peinture'}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {activeTab === 'entretien' 
                ? 'Nos forfaits d\'entretien complets pour garantir la longévité de vos engins.'
                : 'Nos prestations de peinture professionnelle pour redonner vie à vos équipements.'}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {sortedPrestations.map((prestation) => (
              <div 
                key={prestation.id} 
                className="bg-white shadow-lg border-t-4 border-[#F5C500] overflow-hidden"
                data-testid={`prestation-${prestation.equipment_type}`}
              >
                {/* Header */}
                <div 
                  className="p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                  onClick={() => toggleCard(prestation.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-[#F5C500] flex items-center justify-center">
                        {activeTab === 'entretien' ? (
                          <Wrench size={24} color="#1a1a1a" />
                        ) : (
                          <Paintbrush size={24} color="#1a1a1a" />
                        )}
                      </div>
                      <div>
                        <h3 
                          className="text-xl font-bold text-[#1a1a1a]"
                          style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                        >
                          {prestation.equipment_label}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {prestation.items?.length || 0} prestations incluses
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {prestation.price && (
                        <span 
                          className="text-2xl font-bold text-[#F5C500]"
                          style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                        >
                          {prestation.price}
                        </span>
                      )}
                      {expandedCards[prestation.id] ? (
                        <ChevronUp size={24} className="text-gray-400" />
                      ) : (
                        <ChevronDown size={24} className="text-gray-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className={`transition-all duration-300 overflow-hidden ${
                  expandedCards[prestation.id] ? 'max-h-[2000px]' : 'max-h-0'
                }`}>
                  <div className="px-6 pb-6 border-t border-gray-100">
                    <ul className="mt-4 space-y-3">
                      {prestation.items?.filter(item => item.included).map((item, index) => (
                        <li key={item.id || index} className="flex items-start gap-3">
                          <div className="w-5 h-5 bg-[#F5C500] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Check size={12} color="#1a1a1a" strokeWidth={3} />
                          </div>
                          <span className="text-gray-700">{item.name}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <div className="mt-6 pt-6 border-t border-gray-100">
                      <Link 
                        to="/contact" 
                        className="btn-primary inline-block text-center w-full sm:w-auto"
                        data-testid={`cta-${prestation.equipment_type}`}
                      >
                        Demander un Devis
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {sortedPrestations.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              Aucune prestation disponible pour le moment.
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#1a1a1a] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 
            className="text-3xl md:text-4xl font-bold text-white mb-6"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            Besoin d'un Devis Personnalisé ?
          </h2>
          <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
            Chaque engin est unique. Contactez-nous pour un devis adapté à vos besoins spécifiques.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-primary">
              Demander un Devis
            </Link>
            <a href="tel:0693526449" className="btn-secondary flex items-center justify-center gap-2">
              <Phone size={20} />
              06 93 52 64 49
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Prestations;
